import callApi from './callApi';

// NAMING //
/*
  POST = create
  GET = get
  PUT/PATCH = update
  dropdown/options/selection lists = ...Options
*/

export const getIdentity = () => callApi(`/user/identity`, 'GET');

export const getEncumbrances = () => callApi(`/student/encumbrances`, 'GET');

export const search = (
  query = '',
  include = [],
  exclude = [],
  entityType = 'link',
) =>
  callApi('/search', 'POST', {
    query,
    include,
    exclude,
    entityType,
  });

export const searchSuggestions = (query) =>
  callApi(`/search/suggestions?q=${query}`, 'GET');

// Links
export const createLink = async (link) => callApi('/links', 'POST', link);
export const updateLink = (link) => callApi(`/links/${link.id}`, 'PUT', link);
export const disableLink = (id, permanent = false) =>
  callApi(`/links/${id}?permanent=${!!permanent}`, 'DELETE');
export const getLink = (id) => callApi(`/links/${id}`, 'GET');

// Notifications
export const getNotifications = (type) =>
  callApi(`/notifications/${type}`, 'GET');
export const createNotification = (notification) =>
  callApi(`/notifications/`, 'POST', notification);
export const updateNotification = (notification) =>
  callApi(`/notifications/${notification.id}`, 'PUT', notification);
export const deleteNotification = (id) =>
  callApi(`/notifications/${id}`, 'DELETE');

// Home
export const getUserEvents = async (unitOfferingIds) => {
  const query = unitOfferingIds
    ? `&unitOfferingIds=${unitOfferingIds.join(',')}`
    : '';
  const resp = await callApi(`/student/userEvents?strict=false${query}`, 'GET');
  const sorted = resp.events
    .filter((x) => !!x.end.time)
    .sort((a, b) => a.start.time - b.start.time);
  return {
    ...resp,
    events: sorted,
  };
};

export const getLibraryData = (offset = 0, limit = 100) =>
  callApi(`/student/library?offset=${offset}&limit=${limit}`, 'GET');

// Profile
export const getAcademicResults = () =>
  callApi(`/student/academicResults`, 'GET');
export const getCourseProgression = () =>
  callApi(`/student/courseProgression`, 'GET');
export const getCurrentEnrolments = () =>
  callApi(`/student/currentEnrolments`, 'GET');
export const getProfileData = () => callApi(`/student/profile`, 'GET');
export const getPronounsOptions = () => callApi(`/user/pronouns`, 'GET');
export const updatePronouns = (pronouns) =>
  callApi(`/student/pronouns`, 'PATCH', pronouns);
export const updateUnitEnrolments = (course, units) =>
  callApi(`/student/unitEnrolments`, 'PATCH', {
    courseCode: course,
    unitEnrolments: units,
  });

// Upload Services
export const postCustomImage = (
  fileName = null,
  ID = null,
  encodedValue = null,
) =>
  callApi(`/upload/postCustomImage`, 'POST', {
    fileName,
    ID,
    encodedValue,
  });

export const deleteCustomImage = (ID) =>
  callApi(`/upload/deleteCustomImage/${ID}`, 'DELETE');

export const getCustomImage = (ID) =>
  callApi(`/upload/getCustomImage/${ID}`, 'GET');

// User preferences
export const getAvatar = () => callApi(`/user/preferences/avatar`, 'GET');
export const updateAvatar = (avatar) =>
  callApi(`/user/preferences/avatar`, 'PUT', avatar);
export const deleteAvatar = () => callApi(`/user/preferences/avatar`, 'DELETE');
export const getUnitColours = async (unitCodes) => {
  const query = unitCodes ? `?unitCodes=${unitCodes.join(',')}` : '';
  return callApi(`/user/preferences/unitColours/${query}`, 'GET');
};
export const updateUnitColours = (data) =>
  callApi(`/user/preferences/unitColours/`, 'PATCH', data);
export const getDefaultPage = (location) =>
  callApi(
    `/user/preferences/defaultPage/${location ? `?location=${location}` : ''}`,
    'GET',
  );

export const addDefaultPages = (primary = null, minis = []) =>
  callApi(`/user/preferences/addDefaultPages`, 'POST', { primary, minis });

// Portal preferences
export const getPortalPreferences = () => callApi(`/user/preferences`, 'GET');
export const updatePortalPreferences = (data) =>
  callApi(`/user/preferences`, 'PATCH', data);

// App resources
export const getAvatarOptions = () => callApi(`/appResources/avatars`, 'GET');
export const getKeyDates = () => callApi(`/appResources/keyDates`, 'GET');
export const getWeeks = () => callApi(`/appResources/weeks`, 'GET');
export const getUnitColourMappings = () =>
  callApi(`/appResources/unitColourMappings`, 'GET');
export const getImportantDates = (location) =>
  callApi(
    `/appResources/importantDates/${location ? `?location=${location}` : ''}`,
    'GET',
  );

// Impersonation
export const startImpersonation = async (studentId) =>
  callApi(`/impersonation/?studentId=${studentId}`, 'PUT');
export const checkStudentImpersonation = async (studentId) =>
  callApi(`/impersonation/?studentId=${studentId}`, 'GET');
export const isImpersonating = () => callApi(`/impersonation/`, 'GET');
export const endImpersonation = () => callApi(`/impersonation/`, 'DELETE');

// User groups
export const getUserGroups = (usage) => callApi(`/user/groups/${usage}`, 'GET');

// API docs
export const getApiDocs = () =>
  callApi(null, 'GET', null, 'https://admin.staff-dev.monash/apis/');

export const getWallpaperList = () =>
  callApi('/storage/themes/wallpaperList', 'GET', null);

// Feedback
export const submitFeedback = (data) =>
  callApi(`/feedback/`, 'POST', data, null, true);

// Admin
export const getIdentityAdmin = (id, idType = 'studentId') =>
  callApi(`/admin/identity?${idType}=${id}`, 'GET');

// Updates
export const createUpdate = (update, publish = false) =>
  callApi(`/updates${publish ? '?publish=true' : ''}`, 'POST', update);
export const editUpdate = (id, update, publish = false) =>
  callApi(`/updates/${id}${publish ? '?publish=true' : ''}`, 'PUT', update);
export const deleteUpdate = (id) => callApi(`/updates/${id}`, 'delete');
export const getUpdates = () => callApi('/updates', 'GET');
export const getArchivedUpdates = () => callApi('/updates/archive', 'GET');

// Maintenance
export const checkMaintenance = () => callApi('/config/maintenance', 'GET');

// Mocks
export const getMockUserIdentities = () =>
  callApi('/mocks/mockUserIdentities', 'GET');
export const getMockApis = (uid) =>
  callApi(`/mocks/mockApis?uid=${uid}`, 'GET');

export const apis = {
  search,
  searchSuggestions,

  // links
  createLink,
  updateLink,
  disableLink,
  getLink,

  // notifications
  getNotifications,
  createNotification,
  updateNotification,
  deleteNotification,

  // home
  getUserEvents,

  getLibraryData,

  // academic results currently just to get unit titles in the frontend
  getAcademicResults,

  // profile
  getCourseProgression,
  getCurrentEnrolments,
  getProfileData,
  getPronounsOptions,
  updatePronouns,
  updateUnitEnrolments,

  // uploads
  postCustomImage,
  getCustomImage,
  deleteCustomImage,

  // user preferences
  getAvatar,
  updateAvatar,
  deleteAvatar,
  getUnitColours,
  updateUnitColours,
  getDefaultPage,
  addDefaultPages,

  // portal preferences
  getPortalPreferences,
  updatePortalPreferences,

  // app resources
  getAvatarOptions,
  getKeyDates,
  getWeeks,
  getUnitColourMappings,
  getImportantDates,

  // impersonation
  startImpersonation,
  checkStudentImpersonation,
  isImpersonating,
  endImpersonation,

  // user groups
  getUserGroups,

  // api docs
  getApiDocs,

  getWallpaperList,

  // feedback
  submitFeedback,

  // admin
  getIdentity: getIdentityAdmin,

  // updates
  createUpdate,
  editUpdate,
  deleteUpdate,
  getUpdates,
  getArchivedUpdates,

  // maintenance
  checkMaintenance,

  // mocks
  getMockUserIdentities,
  getMockApis,
};

// Reverse map to retrieve the original API function name from a given API function.
// Our mocks live in an object where key = API name and value = mock response.
// However, function names are mangled after building a production bundle.
// Therefore, this map lets us look up the original API name from an API function that is supplied to useApi,
// enabling mocks to work with the useApi hook.
export const apiNameReverseMap = new Map(
  Object.entries(apis).map(([apiName, func]) => [func, apiName]),
);
